import React, { useState, useRef, useEffect, useImperativeHandle, useCallback } from "react";
import { Grid } from "@mui/material";
import { useParams, useHistory } from "react-router-dom";
import { cloneDeep } from "lodash";
import NonMemberContext from "@icoach/nonMember/components/NonMemberContext";
import InviteFriendsBox from "@icoach/nonMember/components/InviteFriendsBox";
import FreeSoloCreateOptionDialog from "@icoach/nonMember/customer/FreeSoloCreateOptionDialog";
import { NON_MEMBER_ROUTER } from "@icoach/router/routerPath";
import AppointmentFormDialog, { AppointmentTimelineBox } from "@icoach/landingPage/dialog/AppointmentFormDialog";
import { EmployeeSelect } from "@icoach/components/";
import useCustomerApi from "@apis/useCustomerApi";
import useAppointmentApi from "@apis/useAppointmentApi";
import { Card, TextField, Typography, CheckboxField, Button, DateField, Stack, SelectField, MenuItem, FormControlLabel, Switch, Alert, Box } from "@curves/ui-base";
import { SourceTypeSelectField } from "@icoach/components/mutipleSelectField/";
import { refIsRequiredError, isEqual } from "@util/utils";
import { parseDate } from "@util/moment";
import { FILTER_TYPE } from "@constants/";
import useMessageDialog from "@util/hook/useMessageDialog";

// 右側預約單畫面
const AppointmentBox = React.forwardRef((props, ref) => {
	const { options } = props;
	const [isApo, setApo] = useState(true);
	const appointmentRef = useRef(null);
	const nonApoReasonRef = useRef(null);
	const contactNotesRef = useRef(null);
	const friendsRef = useRef(null);
	const { originOption, noApoOption } = options;

	const noApoAction = () => {
		const isError = () => {
			return refIsRequiredError(nonApoReasonRef, contactNotesRef);
		};
		const getParams = () => {
			return {
				nonApo: {
					nonApoReason: nonApoReasonRef.current.getResult(),
					contactNotes: contactNotesRef.current.getResult(),
				},
			};
		};
		return { getParams, isError };
	};

	const apoAction = () => {
		const isError = () => refIsRequiredError(appointmentRef, friendsRef);

		const getParams = () => {
			let paramsData = {
				apo: { ...appointmentRef.current.getResult() },
			};
			if (!friendsRef.current.isNull()) {
				Object.assign(paramsData, { friends: [...friendsRef.current.getResult()] });
			}
			return paramsData;
		};

		return { getParams, isError };
	};

	const handleApoSwitch = e => setApo(!isApo);

	useImperativeHandle(
		ref,
		() => {
			let target;
			if (isApo) target = apoAction();
			else target = noApoAction();
			return {
				isError: () => target.isError(),
				getResult: () => ({ ...cloneDeep(target.getParams()) }),
			};
		},
		// eslint-disable-next-line
		[isApo]
	);

	return (
		<Grid container spacing={3}>
			<Grid xs={12} item>
				<Box className="customer-add-isapo-switch-panel">
					<span className="pr-2">是否邀約</span>
					<FormControlLabel control={<Switch defaultChecked={isApo} onChange={handleApoSwitch} />} label="APO" />
				</Box>
			</Grid>
			{isApo ? (
				<Grid xs={12} item>
					<Stack direction="column" spacing={2}>
						<AppointmentTimelineBox ref={appointmentRef} />
						<InviteFriendsBox options={originOption} ref={friendsRef} />
					</Stack>
				</Grid>
			) : (
				<React.Fragment key={2}>
					<Grid xs={6} item>
						<SelectField ref={nonApoReasonRef} label="未APO原因" displayEmpty fullWidth required>
							<MenuItem disabled value="">
								<em>請選擇</em>
							</MenuItem>
							{noApoOption.map(({ text, value, disabled }) => (
								<MenuItem value={value} key={text} disabled={disabled}>
									{text}
								</MenuItem>
							))}
						</SelectField>
					</Grid>
					<Grid xs={12} item>
						<TextField label="聯絡備註" ref={contactNotesRef} rows={2} multiline fullWidth />
					</Grid>
				</React.Fragment>
			)}
		</Grid>
	);
});

// 左側 包含 主要邏輯
const CustomerAddInquiryPage = () => {
	const setMessageDialog = useMessageDialog();
	const [isMounted, setMounted] = useState(false);
	const [sourceData, setSourceData] = useState({});
	const [memberData, setMembetData] = useState({}); // 無效會員用
	const [open, setOpen] = useState(false);
	const { id: customerIDParams = 0 } = useParams();
	const { getCutomnerOptionsApi, postCustomersApi } = useCustomerApi();
	const { postAppointmentCheckApi } = useAppointmentApi();
	const history = useHistory();
	const checkInDateRef = useRef(null);
	const coachRef = useRef(null);
	const peopleInsertRef = useRef(null);

	const sourceTypeSelectorRef = useRef(null);
	const neededRef = useRef(null);
	const askRecordTypeRef = useRef(null);
	const introducerRef = useRef(null);
	const noteRef = useRef(null);
	const jobCheckedRef = useRef([]);
	const appointmentRef = useRef(null);
	const invalidMemberRefreshRef = useRef(null);
	const { resource = {} } = sourceData;
	const {
		sourceType = null,
		customerType = null,
		sourceTag = null,
		requiredType = null,
		askRecordType = null,
		introducer = "",
		notes = "",
		hasLocation = false,
		hasPush = false,
		hasThanks = false,
	} = sourceData;
	const {
		askRecordTypes: askRecordOption = [],
		requiredTypes: demandOption = [],
		coachOptions: employeeOption = [],
		sourceTypes: originOption = [],
		peopleOptions: peopleOption = [],
		customerNonApoTypes: noApoOption = [],
	} = resource;

	const switchRouterPath = type => {
		let path = NON_MEMBER_ROUTER.CUSTOMER.OVERVIEW;
		if (isEqual(type, FILTER_TYPE.GUESTOVERVIEW)) {
			path = NON_MEMBER_ROUTER.PROSPECT.OVERVIEW;
		}
		try {
			const raw = localStorage.getItem("nonmember-scroller");
			if (!raw) {
				history.push(path);
				return null;
			}
			const parsed = JSON.parse(raw);
			const q = parsed?.query;
			if (typeof q === "string" && q.trim()) {
				path = `${path}?${q}&mode=load`;
			}
		} catch (err) {
		} finally {;
			history.push(path);
		}
	};

	// 切換無效會員彈跳視窗
	const handleNameChange = (eventName, targetData) => {
		if (!targetData) return;
		const { isMember } = targetData.option;
		if (isEqual(eventName, "selectOption") && isMember) {
			let cloneData = targetData.option;
			cloneData.peopleID = cloneData.id;
			cloneData.peopleType = 2;
			peopleInsertRef.current.resetValue();
			setMessageDialog({
				open: true,
				title: "無效會員",
				msg: "此為無效會員，需轉跳到會員預約，是否要轉倒過去？",
				onAsyncConfirm: (e, onclose) => {
					setMembetData(cloneData);
					setOpen(true);
					onclose();
				},
			});
		} else {
			const { id } = targetData.option;
			initOptionsApi(id);
		}
	};

	const handleBackToOverviewOnClick = useCallback(
		e => {
			switchRouterPath(customerType);
		},
		// eslint-disable-next-line
		[customerType]
	);

	const handleAddOnClick = e => {
		let params = {};
		if (!refIsRequiredError(peopleInsertRef, sourceTypeSelectorRef, neededRef, introducerRef, askRecordTypeRef, noteRef, coachRef, appointmentRef, checkInDateRef)) {
			let jobs = jobCheckedRef.current.getResult();
			let appointments = appointmentRef.current.getResult();
			let people = peopleInsertRef.current.getResult();
			let sourceTypes = sourceTypeSelectorRef.current.getResult();
			const customerPar = {
				...people,
				...sourceTypes,
				askRecordType: askRecordTypeRef.current.getResult(),
				requiredType: neededRef.current.getResult(),
				introducer: introducerRef.current.getResult(),
				notes: noteRef.current.getResult(),
			};
			jobs = jobs.reduce((acc, cur) => ({ ...acc, [cur.value]: cur.checked }), {});
			params = Object.assign({}, params, { customer: { ...customerPar } });
			params = Object.assign({}, params, appointments);
			params = Object.assign({}, params, jobs);
			if (params.friends) {
				params.friends = params.friends.map(targetItem => {
					let introducerName = people.name;
					if (!introducerName) {
						introducerName = peopleOption.find(({ id: tarID }) => isEqual(tarID, people.customerID))["name"];
					}
					return {
						...targetItem,
						askRecordType: askRecordTypeRef.current.getResult(),
						requiredType: neededRef.current.getResult(),
						introducer: introducerName,
					};
				});
			}
			params = Object.assign({}, params, {
				employeeID: coachRef.current.getResult(),
				askDate: checkInDateRef.current.getResult(),
				registrationDate: parseDate(),
			});

			addAppointmentApi(params);
		} else {
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		}
	};

	// 新增詢問API
	const addAppointmentApi = async params => {
		// 非 顧客/名單  新增新顧客
		if (!params.customer.customerID) {
			const resp = await postCustomersApi(params);
			if (resp) {
				history.push(NON_MEMBER_ROUTER.CUSTOMER.OVERVIEW);
			}
			return null;
		}

		const isApo = await postAppointmentCheckApi({
			peopleID: params.customer.customerID,
			peopleType: 1,
		});
		if (isApo) {
			// 顧客總覽與名單總覽切換
			if (isEqual(isApo, "noAppointmentYet")) {
				const resp = await postCustomersApi(params);
				if (resp) {
					switchRouterPath(customerType);
					return null;
				}
			} else if (Array.isArray(isApo)) {
				setMessageDialog({
					open: true,
					title: "預約單訊息",
					MsgComp: () => (
						<Alert color="info" variant="filled" severity="warning">
							<Typography>
								此顧客已預約FA
								<br />
								已預約的日期：
								<br />
								{isApo.map(dateString => (
									<Typography key={dateString} className="pl-5" variant="h5" component="span">
										{dateString}
										<br />
									</Typography>
								))}
							</Typography>
							<Typography>確定要新增預約？？</Typography>
						</Alert>
					),
					// 情況三：更新現有顧客（有預約衝突Dialog）
					onAsyncConfirm: async (e, closeFn) => {
						const resp = await postCustomersApi(params);
						resp && history.push(NON_MEMBER_ROUTER.CUSTOMER.OVERVIEW);
						closeFn();
					},
				});
			}
		}
	};

	const initOptionsApi = async cID => {
		let resp = await getCutomnerOptionsApi(cID);
		if (resp) {
			setSourceData(resp);
			setMounted(true);
		}
	};

	const childContext = {
		getOriginType: () => sourceTypeSelectorRef.current.getResult(),
	};

	// 無效會員處理
	useEffect(
		() => {
			initOptionsApi(customerIDParams);
			invalidMemberRefreshRef.current = () => history.push(NON_MEMBER_ROUTER.CUSTOMER.OVERVIEW);
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<NonMemberContext.Provider value={childContext}>
			<Box className="container main-container-spacing">
				<Box component="header" className="page-header">
					<Typography className="page-header-title" variant="h3">
						新增詢問
					</Typography>
				</Box>
				<Card>
					<Card.CardContent className="customer-add-apo-header">
						<Grid container spacing={3}>
							<Grid item xs={4}>
								<DateField label="詢問日期" placeholder="YYYY/MM/DD" ref={checkInDateRef} defaultValue={parseDate()} lightColor fullWidth required />
							</Grid>
							<Grid item xs={4} />
							<Grid item xs={4}>
								<EmployeeSelect ref={coachRef} options={employeeOption} label="擔當" lightColor fullWidth required />
							</Grid>
						</Grid>
					</Card.CardContent>
					{isMounted && (
						<>
							<Card.CardContent className="p-4">
								<Grid container spacing={3}>
									<Grid xs={6} item>
										<Typography gutterBottom>基本資料</Typography>
										<Grid container spacing={3}>
											<Grid item xs={12}>
												<FreeSoloCreateOptionDialog
													label="姓名,手機"
													defaultValue={customerIDParams}
													options={peopleOption}
													optionKeys={["name", "mobile"]}
													ref={peopleInsertRef}
													onChange={handleNameChange}
													disabled={!!customerIDParams}
													fullWidth
													required
												/>
											</Grid>
											<SourceTypeSelectField
												key={`${sourceType}-${sourceTag}`}
												ref={sourceTypeSelectorRef}
												defaultSourceType={sourceType || null}
												defaultSourceTag={sourceTag || null}
												options={originOption}
												gridSize={12}
											/>
											<Grid item xs={6}>
												<SelectField key={requiredType} ref={neededRef} label="需求" defaultValue={requiredType || ""} fullWidth displayEmpty required>
													<MenuItem disabled value="">
														<em>請選擇</em>
													</MenuItem>
													{demandOption.map(({ text, value, disabled }) => (
														<MenuItem value={value} key={value} disabled={disabled}>
															{text}
														</MenuItem>
													))}
												</SelectField>
											</Grid>
											<Grid item xs={6}>
												<SelectField key={askRecordType} ref={askRecordTypeRef} label="詢問方式" defaultValue={askRecordType || ""} fullWidth displayEmpty required>
													<MenuItem disabled value="">
														<em>請選擇</em>
													</MenuItem>
													{askRecordOption.map(({ text, value, disabled }) => (
														<MenuItem value={value} key={value} disabled={disabled}>
															{text}
														</MenuItem>
													))}
												</SelectField>
											</Grid>
											<Grid item xs={6}>
												<TextField key={introducer} label="介紹人" ref={introducerRef} defaultValue={introducer} fullWidth />
											</Grid>
											<Grid item xs={12}>
												<TextField key={notes} label="備註" rows={2} ref={noteRef} defaultValue={notes} fullWidth multiline />
											</Grid>
										</Grid>
									</Grid>
									<Grid xs={6} item>
										<AppointmentBox ref={appointmentRef} options={{ originOption, noApoOption }} />
									</Grid>
								</Grid>
							</Card.CardContent>
							<Card.CardContent className="px-3 py-4">
								<Box className="customer-add-reply-checkbox-panel">
									<CheckboxField defaultChecked={[hasPush, hasLocation, hasThanks]} ref={jobCheckedRef} origin="end" fullWidth row>
										<CheckboxField.Checkbox value="hasPush" label="推進" />
										<CheckboxField.Checkbox value="hasLocation" label="位置說明" />
										<CheckboxField.Checkbox value="hasThanks" label="答謝" />
									</CheckboxField>
								</Box>
							</Card.CardContent>
							<Card.CardContent className="flex flex-y-flex-end flex-gap-3 pr-4 pb-3">
								<Button variant="outlined" color="secondary" onClick={handleBackToOverviewOnClick}>
									返回
								</Button>
								<Button variant="contained" color="secondary" onClick={handleAddOnClick}>
									新增
								</Button>
							</Card.CardContent>
						</>
					)}
				</Card>
			</Box>
			<AppointmentFormDialog
				open={open}
				onClose={() => setOpen(false)}
				sourceData={{
					initialData: memberData,
				}}
				refresh={invalidMemberRefreshRef}
			/>
		</NonMemberContext.Provider>
	);
};

export default CustomerAddInquiryPage;
